import {Resources} from "../../../../../../../resources/Resources";
import {SiteWhiteLabelAgent} from "../../../../../../../models/site/whitelabel/SiteWhiteLabelAgent";

/**
 * Deux notions dans ce fichier, à ne pas confondre :
 * - le scope, c'est l'écran d'où parle le client (il ne désigne plus une conversation, il n'y en a
 *   qu'une par communauté) ;
 * - le persona, c'est l'agent qui écrit un message donné, transmis par le serveur sur chaque
 *   message et sur chaque événement de tour.
 */
export class CommunityAgentScope {
    public static NONE = 0;
    public static BLOG = 1;
    public static ARTICLE = 2;
    public static CALENDAR = 3;
    public static AUTOPILOT = 4;
    public static COMPETITORS = 5;
    public static RANKING = 6;
    public static TECHNICAL = 7;
    public static CHAT = 8;
    public static OPTIMIZATION = 9;
    public static BACKLINKS = 10;
    public static LLM = 11;
    public static AUDIT = 12;

    public static PERSONA_ALYA = 1;
    public static PERSONA_MARC = 2;
    public static PERSONA_NOX = 3;
    public static PERSONA_MAYA = 4;
    public static PERSONA_REMI = 5;
    public static PERSONA_LOUISE = 6;
    // agents hors du chat : ils n'écrivent aucun message, seul leur visage s'affiche
    public static PERSONA_LUCA = 101;
    public static PERSONA_JULE = 102;
    public static PERSONA_CHRIS = 103;
    public static PERSONA_MAX = 104;

    /**
     * L'agent qui prend naturellement la main sur un écran : sert au dock, avant toute réponse du
     * serveur, pour ne pas afficher un visage au hasard.
     */
    public static personaOfScope(scopeType: number): number {
        if (scopeType == this.COMPETITORS || scopeType == this.RANKING || scopeType == this.LLM) {
            return this.PERSONA_MARC;
        }
        if (scopeType == this.TECHNICAL || scopeType == this.AUDIT) {
            return this.PERSONA_NOX;
        }
        if (scopeType == this.BACKLINKS) {
            return this.PERSONA_MAYA;
        }
        if (scopeType == this.OPTIMIZATION) {
            return this.PERSONA_REMI;
        }
        return this.PERSONA_ALYA;
    }

    public static personaNameById(persona: number): string {
        switch (persona) {
            case this.PERSONA_MARC:
                return "Marc";
            case this.PERSONA_NOX:
                return "Nox";
            case this.PERSONA_MAYA:
                return "Maya";
            case this.PERSONA_REMI:
                return "Rémi";
            case this.PERSONA_LOUISE:
                return "Louise";
            case this.PERSONA_LUCA:
                return "Luca";
            case this.PERSONA_JULE:
                return "Jule";
            case this.PERSONA_CHRIS:
                return "Chris";
            case this.PERSONA_MAX:
                return "Max";
            default:
                return "Alya";
        }
    }

    /** La clé stable d'un agent dans les textes (« marc », « louise »…), sans accent ni majuscule. */
    public static personaKeyById(persona: number): string {
        return this.personaNameById(persona).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    }

    public static personaExpertiseById(persona: number): string {
        switch (persona) {
            case this.PERSONA_MARC:
                return Resources.t("words.agentPersonaMarcExpertise");
            case this.PERSONA_NOX:
                return Resources.t("words.agentPersonaNoxExpertise");
            case this.PERSONA_MAYA:
                return Resources.t("words.agentPersonaMayaExpertise");
            case this.PERSONA_REMI:
                return Resources.t("words.agentPersonaRemiExpertise");
            case this.PERSONA_LOUISE:
                return Resources.t("words.agentPersonaLouiseExpertise");
            default:
                return Resources.t("words.agentPersonaAlyaExpertise");
        }
    }

    /**
     * L'image passe par le catalogue marque blanche : un site qui a ses propres avatars doit les
     * voir dans la conversation comme il les voit dans la navigation.
     */
    public static personaImageById(persona: number): string {
        return Resources.agentImageUrl(this.whiteLabelAgent(persona));
    }

    /**
     * Une marque blanche qui a posé son propre visage sur cet agent le garde : l'avatar animé ne
     * remplace que les visages Sedestral.
     */
    public static personaHasCustomImage(persona: number): boolean {
        return Resources.agentHasCustomImage(this.whiteLabelAgent(persona));
    }

    /**
     * Le persona d'un visage de la navigation ; aucun pour l'équipe entière ou un agent hors du chat.
     */
    public static personaOfAgent(agent: SiteWhiteLabelAgent): number {
        switch (agent) {
            case SiteWhiteLabelAgent.NAV_ALYA:
            case SiteWhiteLabelAgent.AGENT_ALYA:
                return this.PERSONA_ALYA;
            case SiteWhiteLabelAgent.NAV_MARC:
            case SiteWhiteLabelAgent.AGENT_MARC:
                return this.PERSONA_MARC;
            case SiteWhiteLabelAgent.NAV_NOX:
            case SiteWhiteLabelAgent.AGENT_NOX:
                return this.PERSONA_NOX;
            case SiteWhiteLabelAgent.NAV_LUCA:
                return this.PERSONA_LUCA;
            case SiteWhiteLabelAgent.NAV_JULE:
                return this.PERSONA_JULE;
            case SiteWhiteLabelAgent.NAV_CHRIS:
                return this.PERSONA_CHRIS;
            case SiteWhiteLabelAgent.NAV_MAX:
                return this.PERSONA_MAX;
            case SiteWhiteLabelAgent.NAV_MAYA:
            case SiteWhiteLabelAgent.AGENT_MAYA:
                return this.PERSONA_MAYA;
            case SiteWhiteLabelAgent.NAV_REMI:
            case SiteWhiteLabelAgent.AGENT_REMI:
                return this.PERSONA_REMI;
            case SiteWhiteLabelAgent.NAV_LOUISE:
                return this.PERSONA_LOUISE;
            default:
                return undefined;
        }
    }

    private static whiteLabelAgent(persona: number): SiteWhiteLabelAgent {
        switch (persona) {
            case this.PERSONA_MARC:
                return SiteWhiteLabelAgent.NAV_MARC;
            case this.PERSONA_NOX:
                return SiteWhiteLabelAgent.NAV_NOX;
            case this.PERSONA_MAYA:
                return SiteWhiteLabelAgent.NAV_MAYA;
            case this.PERSONA_REMI:
                return SiteWhiteLabelAgent.NAV_REMI;
            case this.PERSONA_LUCA:
                return SiteWhiteLabelAgent.NAV_LUCA;
            case this.PERSONA_JULE:
                return SiteWhiteLabelAgent.NAV_JULE;
            case this.PERSONA_CHRIS:
                return SiteWhiteLabelAgent.NAV_CHRIS;
            case this.PERSONA_MAX:
                return SiteWhiteLabelAgent.NAV_MAX;
            case this.PERSONA_LOUISE:
                return SiteWhiteLabelAgent.NAV_LOUISE;
            default:
                return SiteWhiteLabelAgent.NAV_ALYA;
        }
    }
}
